import 'url-search-params-polyfill'
import React, { lazy, useEffect } from 'react'
import { Stop } from '@mortgage-pos/ui/components/Stop'
import { Page } from '@mortgage-pos/ui/components/Page'
import incense from '@mortgage-pos/ui/incense'
import Loading from '@mortgage-pos/ui/components/Loading'
import 'react-toastify/dist/ReactToastify.min.css'
import { ToastContainer } from 'react-toastify'
import { useStoreState, useStoreActions } from '@mortgage-pos/ui/store'
import NTLQualification from '@mortgage-pos/ui/pages/NTLQualification'
import ErrorBoundary from '@mortgage-pos/ui/components/ErrorBoundary'
import PageTransition from '@mortgage-pos/ui/components/PageTransition'
import usePhoneAuthRedirect from '@mortgage-pos/ui/hooks/usePhoneAuthRedirect'
import useDefaultQuestionSetRedirect from '@mortgage-pos/ui/hooks/useDefaultQuestionSetRedirect'
import usePhoneFlowRedirect from '@mortgage-pos/ui/hooks/usePhoneFlowRedirect'
import useLoanOfficerPopUp from './useLoanOfficerPopUp'
import useQuestionnaireInit from './useQuestionnaireInit'
import useFetchLoanOfficer from './useFetchLoanOfficer'
import useQuestionnaireFinished from './useQuestionnaireFinished'
import useBackButtonNavigation from './useBackButtonNavigation'
import useFetchUserLocation from './useFetchUserLocation'
import { useLocation, useNavigate } from 'react-router-dom'
import useApplicationInit from './useApplicationInit'
import useSsnAuthRedirect from '@mortgage-pos/ui/hooks/useSsnAuthRedirect'
const LottieAnimation = lazy(
  () => import('@mortgage-pos/ui/components/LottieAnimation')
)

const useQuery = () => {
  return new URLSearchParams(useLocation().search)
}

const QuestionnaireMap = {
  page: Page,
  stop: Stop,
  ntlQualification: NTLQualification,
}

export const Questionnaire = () => {
  const query = useQuery()
  const navigate = useNavigate()
  const location = useLocation()
  const continueIndex = parseInt(query.get('continue-index') || '0', 10)

  const { active, pages } = useStoreState((state) => state.questionnaire)
  const { isCheckingAuthentication, isAuthenticated } = useStoreState(
    (state) => state.authentication
  )
  const { isPrefillingAnswers } = useStoreState((store) => store.answers)
  const { isLoadingAlfalfaFeatures } = useStoreState(
    (state) => state.alfalfaExtendedFeatures
  )

  const { answer, setPageIndex } = useStoreActions(
    (state) => state.questionnaire
  )

  useLoanOfficerPopUp()
  useQuestionnaireInit()
  useApplicationInit()
  useFetchLoanOfficer()
  useDefaultQuestionSetRedirect()
  usePhoneFlowRedirect()
  useQuestionnaireFinished()
  const { isCheckingForAccount } = usePhoneAuthRedirect()
  const { isCheckingForAccountFromSsn } = useSsnAuthRedirect()
  useBackButtonNavigation()
  useFetchUserLocation()

  useEffect(() => {
    if (active && pages && continueIndex > 0 && continueIndex < pages.length) {
      setPageIndex(continueIndex)

      // Remove the continue-index parameter from the URL
      const newQuery = new URLSearchParams(query)
      newQuery.delete('continue-index')
      navigate(
        {
          pathname: location.pathname,
          search: newQuery.toString(),
        },
        { replace: true }
      )
    }
  }, [
    active,
    pages,
    continueIndex,
    setPageIndex,
    navigate,
    location.pathname,
    query,
  ])

  if (
    !active ||
    isCheckingForAccount ||
    isCheckingForAccountFromSsn ||
    isCheckingAuthentication ||
    (isAuthenticated && isPrefillingAnswers)
  ) {
    return <Loading />
  }

  if (typeof QuestionnaireMap[active.type] === 'undefined') {
    incense()
      .details({
        name: 'QuestionnaireNtlComponentError',
        message: 'Failed to load component',
      })
      .safe({
        componentType: active.type,
        appPath: 'QuestionnaireNtl.tsx',
      })
      .error()

    return (
      <div className="error-message">
        Oops! Looks like an error occurred. We've alerted the troops and should
        be back up shortly.
      </div>
    )
  }

  return (
    <ErrorBoundary>
      <section
        className={`app-content__wrap ${active.modifierClass || ''}`}
        data-testid="1003-questionnaire"
      >
        {isLoadingAlfalfaFeatures ? (
          <LottieAnimation animationName="rocket" />
        ) : null}

        <PageTransition>
          {React.createElement(QuestionnaireMap[active.type], {
            key: active.name,
            page: active,
            answer,
          })}
        </PageTransition>

        <ToastContainer
          position="bottom-right"
          autoClose={false}
          hideProgressBar
          closeOnClick={false}
          draggable
          limit={1}
        />
      </section>
    </ErrorBoundary>
  )
}

export default Questionnaire
